import React, { useState, useEffect } from 'react';
import { useAuth } from '../../systems/auth-context';
import { isSsoOnlyHostname } from '../../systems/sso-only-host';
import { Spinner } from '../spinner';
import styles from './auth.module.css';
import { startAuthentication } from '@simplewebauthn/browser';

interface LoginProps {
  onSwitchToRegisterUser?: () => void;
  onSwitchToRegisterEnterprise?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSwitchToRegisterUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [oidcStatusLoaded, setOidcStatusLoaded] = useState(false);
  const ssoOnly = isSsoOnlyHostname(window.location.hostname);
  const { login, sessionNotice, clearSessionNotice } = useAuth();

  useEffect(() => {
    if (sessionNotice) {
      setError(sessionNotice);
      clearSessionNotice();
    }
  }, [sessionNotice, clearSessionNotice]);

  useEffect(() => {
    fetch('/api/auth/oidc/status')
      .then((res) => res.json())
      .then((data) => setOidcEnabled(Boolean(data.enabled)))
      .catch(() => setOidcEnabled(false))
      .finally(() => setOidcStatusLoaded(true));
  }, []);

  const handleOidcLogin = () => {
    window.location.href = '/api/auth/oidc';
  };

  const handlePasskeyLogin = async () => {
    setIsAuthenticating(true);
    setError('');

    try {
      // Step 1: Get authentication options (email is optional for discoverable credentials)
      const optionsRes = await fetch('/api/passkey/login-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email || undefined }),
      });
      const data = await optionsRes.json();

      if (data.error) throw new Error(data.error);

      // Step 2: Start authentication with the browser
      const assertionResponse = await startAuthentication(data.options);

      // Step 3: Verify authentication on server
      const verifyRes = await fetch('/api/passkey/login-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: assertionResponse, challengeId: data.challengeId }),
      });

      const verification = await verifyRes.json();
      if (verification.verified) {
        login(verification.user);
      } else {
        throw new Error(verification.error || 'La connexion a échoué');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      login(data.user);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isSubmitting || isAuthenticating;

  if (ssoOnly) {
    return (
      <div className={styles.authContainer}>
        <h2>Connexion</h2>
        {error && <div className={styles.error}>{error}</div>}
        {!oidcStatusLoaded ? (
          <Spinner size="sm" label="Chargement…" />
        ) : oidcEnabled ? (
          <button type="button" className={styles.ssoBtn} onClick={handleOidcLogin}>
            Se connecter avec le SSO de votre organisation
          </button>
        ) : (
          <div className={styles.error}>Le SSO n&apos;est pas disponible pour le moment.</div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.authContainer}>
      <h2>Connexion</h2>
      {error && <div className={styles.error}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className={styles.inputGroup}>
          <label>Email</label>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            required 
          />
        </div>
        <div className={styles.inputGroup}>
          <label>Mot de passe</label>
          <input 
            type="password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            required 
          />
        </div>
        <button type="submit" className={styles.submitBtn} disabled={isBusy}>
          {isSubmitting ? (
            <Spinner size="sm" label="Connexion…" />
          ) : (
            'Se connecter'
          )}
        </button>
      </form>

      <div className={styles.passkeyDivider}>
        <span>OU</span>
      </div>

      {oidcEnabled && (
        <button type="button" className={styles.ssoBtn} onClick={handleOidcLogin}>
          Se connecter avec le SSO de votre organisation
        </button>
      )}

      <button 
        className={styles.passkeyBtn} 
        onClick={handlePasskeyLogin}
        disabled={isBusy}
      >
        {isAuthenticating ? (
          <Spinner size="sm" label="Connexion…" />
        ) : (
          <>
            <span>🔑</span> Se connecter avec une Passkey
          </>
        )}
      </button>

      <div className={styles.securityInfo}>
        L'utilisation d'un gestionnaire de mots de passe comme <strong>Proton Pass</strong> permet de créer des comptes plus sécurisés et de protéger votre vie privée.
      </div>
      <div className={styles.switchLinks}>
        {onSwitchToRegisterUser && (
          <button type="button" onClick={onSwitchToRegisterUser} className={styles.linkBtn}>
            Créer un compte
          </button>
        )}
      </div>
    </div>
  );
};

export default Login;
